import { injectable } from 'inversify';
import { Term } from '@/app/shared/models';
import { removeHtmlTags } from '@/app/shared/utils/content-utils';

export interface TermsByLetterAndTopic {
  [letter: string]: {
    [topicId: string]: Term[];
  };
}

const SPLIT_SEPARATOR: string = ' ';

@injectable()
export class TermService {
  public termsByLetterAndTopicId(terms: Term[], search: string|null = null): TermsByLetterAndTopic {
    const result: TermsByLetterAndTopic = {};

    if (search !== null) {
      search = search.toLocaleLowerCase();
    }

    terms.forEach((term) => {
      const titleWithoutHtml: string = removeHtmlTags(term.title);
      const letter: string = titleWithoutHtml.substring(0, 1).toLocaleUpperCase();
      if (!result[letter]) {
        result[letter] = {};
      }

      term.topicIds.forEach((topic) => {
        const searchContains: boolean = search !== null && this.contains(titleWithoutHtml, search);
        if (search === null || searchContains) {
          if (!result[letter][topic]) {
            result[letter][topic] = [];
          }

          if (search !== null && searchContains) {
            term.title = this.markFragment(titleWithoutHtml, search);
          }

          result[letter][topic] = [...result[letter][topic], term];
        }
      });
    });

    const ordered: TermsByLetterAndTopic = {};
    Object.keys(result)
      .sort((a, b) => a.localeCompare(b))
      .forEach((key) => {
        if (Object.keys(result[key]).length > 0) {
          ordered[key] = result[key];
        }
      });

    return ordered;
  }

  private contains(content: string, search: string): boolean {
    if (this.searchContainsSeparator(search)) {
      return content.toLocaleLowerCase().startsWith(search);
    }

    const items: string[] = content.split(SPLIT_SEPARATOR);
    for (const item of items) {
      if (item.toLocaleLowerCase().startsWith(search)) {
        return true;
      }
    }

    return false;
  }

  private markFragment(content: string, search: string): string {
    if (this.searchContainsSeparator(search)) {
      return this.markItem(content, search);
    }

    const items: string[] = content.split(SPLIT_SEPARATOR);
    let results: string[] = [];
    for (let item of items) {
      if (item.toLocaleLowerCase().startsWith(search)) {
        item = this.markItem(item, search);
      }

      results = [...results, item];
    }

    return results.join(SPLIT_SEPARATOR);
  }

  private markItem(item: string, search: string): string {
    const regex = new RegExp(`^(${search})`, 'i');
    return item.replace(regex, '<span class="term-title-marked-fragment">$1</span>');
  }

  private searchContainsSeparator(search: string) {
    return search.indexOf(SPLIT_SEPARATOR) !== -1;
  }
}

export const TermServiceSymbol = Symbol.for('TermService');
